import { AttachInternals, Component, ComponentInterface, Element, h, Host, Listen, Prop } from "@stencil/core";
import { ButtonSize, ButtonVariant } from "../../utils/button.enum";
import { ButtonLabelVisibility } from "../../utils/buttonLabelVisibility";

/**
 * @slot (default) - Content is placed inside the button if provided without a slot name
 * @slot "icon" - Should be used to place a custom icon
 */
@Component({
    tag: "ux-button",
    styleUrl: "button.scss",
    shadow: true,
    formAssociated: true
})
export class Button implements ComponentInterface {
    private disabledAttributeObserver!: MutationObserver;
    @Element() host!: HTMLUxButtonElement;

    @AttachInternals() private internals!: ElementInternals;

    /** The type attribute of the button element. */
    @Prop() readonly type?: "button" | "submit" | "reset";

    /** The name attribute of the button element. */
    @Prop({ reflect: true }) readonly name?: string;

    /** If set to `true`, the user cannot interact with the button. */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /** If set to `true`, the button shows a selected state. */
    @Prop({ reflect: true }) readonly selected: boolean = false;

    /** The size of the button. Use `icon` for a button with just an icon. */
    @Prop() readonly size: keyof typeof ButtonSize | ButtonSize = ButtonSize.Default;

    /** Label text of the button, required for better accessibility */
    @Prop() readonly label?: string;

    /** If set to `true`, the label is not truncated. */
    @Prop() readonly multiline: boolean = false;

    /** The name of the icon to show next to the label. */
    @Prop() readonly icon?: string;

    /** Horizontal placement of the icon. */
    @Prop() readonly iconPlacement: "start" | "end" = "start";

    /** Breakpoint when label should be visible */
    @Prop() readonly labelVisibility: keyof typeof ButtonLabelVisibility | ButtonLabelVisibility = ButtonLabelVisibility.Default;

    /** Style variant of the button. Note that not all variants are supported by every theme. */
    @Prop() readonly variant: keyof typeof ButtonVariant | ButtonVariant = ButtonVariant.Default;

    /**
     * Define href for button. If set button will be semantical rendered as link (<a>)
     */
    @Prop({ reflect: true }) readonly href?: string;

    /** If set to `true`, the component adds a download attribute to the a tag */
    @Prop() readonly download: boolean = false;

    /**
     * Define link target, _self, _blank
     * @default _self
     */
    @Prop() readonly target: string = "_self";

    @Listen("click", { capture: true })
    public onClick(e: MouseEvent): void {
        if (this.disabled) {
            e.stopPropagation();
        }
    }

    /** fixes a problem where the attribute disabled="false" is interpreted differently by the browser - preventing events */
    @Listen("disabled")
    private checkDisabled(): void {
        if (!this.disabled) {
            this.host.removeAttribute("disabled");
        }
    }

    connectedCallback(): void {
        this.disabledAttributeObserver = new MutationObserver(() => {
            if (this.host.hasAttribute("disabled")) {
                this.checkDisabled();
            }
        });
        this.disabledAttributeObserver.observe(this.host, { attributes: true, attributeFilter: ["disabled"] });
        this.checkDisabled();
    }

    disconnectedCallback(): void {
        this.disabledAttributeObserver.disconnect();
    }

    private getTabIndex = (): number => {
        if (this.disabled) {
            return -1;
        }
        const tabindexAttr = this.host.getAttribute("tabindex");
        if (tabindexAttr !== null && !Number.isNaN(parseInt(tabindexAttr, 10))) {
            return parseInt(tabindexAttr, 10);
        }
        return 0;
    };

    private dispatchFormSubmit() {
        if (this.type === "submit" && this.internals.form) {
            this.internals.form.requestSubmit();
        }
    }

    render() {
        const baseClass = "ux-button";
        const hasCustomIcon = this.host.querySelector("[slot=icon]") !== null;
        const hasIcon = Boolean(hasCustomIcon || (this.icon && this.icon.length > 0));
        const hideLabel = [ButtonSize.Icon, ButtonSize.IconStretched].includes(this.size as ButtonSize);
        return (
            <Host variant={this.variant} size={this.size} iconPlacement={this.iconPlacement} icon={this.icon}>
                {!this.href && (
                    <button
                        class={{
                            [`${baseClass}__label-visibility ${baseClass}__label-visibility--${this.labelVisibility}`]:
                                !hideLabel && this.labelVisibility !== ButtonLabelVisibility.Default
                        }}
                        tabIndex={this.getTabIndex()}
                        type={this.type}
                        name={this.name}
                        disabled={this.disabled}
                        aria-label={this.label}
                        onClick={() => this.dispatchFormSubmit()}
                    >
                        <span
                            class={{
                                [`${baseClass}__icon`]: hasIcon,
                                [`${baseClass}__icon--none`]: !hasIcon,
                                [`${baseClass}__icon--custom`]: hasCustomIcon
                            }}
                        >
                            <slot name="icon">{this.icon && <ux-icon name={this.icon} />}</slot>
                        </span>
                        <span
                            class={{
                                [`${baseClass}__label`]: !hideLabel,
                                [`${baseClass}__label--truncate`]: !hideLabel && !this.multiline,
                                [`${baseClass}__label--hidden`]: hideLabel
                            }}
                        >
                            <slot>{this.label}</slot>
                        </span>
                    </button>
                )}
                {this.href && (
                    <a
                        part="button"
                        tabIndex={this.getTabIndex()}
                        aria-label={this.label}
                        href={!this.disabled ? this.href : undefined}
                        aria-disabled={this.disabled ? "true" : false}
                        target={this.target}
                        {...(this.download && {
                            download: ""
                        })}
                    >
                        <span
                            aria-hidden="true"
                            class={{
                                [`${baseClass}__icon`]: hasIcon,
                                [`${baseClass}__icon--none`]: !hasIcon,
                                [`${baseClass}__icon--custom`]: hasCustomIcon
                            }}
                        >
                            <slot name="icon">{this.icon && <ux-icon name={this.icon} />}</slot>
                        </span>
                        <span
                            class={{
                                [`${baseClass}__label`]: true,
                                [`${baseClass}__label--truncate`]: !this.multiline,
                                [`${baseClass}__label--hidden`]: this.size === "icon"
                            }}
                        >
                            <slot>{this.label}</slot>
                        </span>
                    </a>
                )}
            </Host>
        );
    }
}
